/**
 * Analytics tracking hook with GDPR consent management
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { trackEvent, type AnalyticsEventType } from "@/api/videoShareApi";

const CONSENT_KEY = "video_share_analytics_consent";

export function useAnalytics(shareKey: string) {
  const [hasConsent, setHasConsent] = useState(false);
  const [hasChoice, setHasChoice] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check for existing consent on mount
  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    setHasChoice(consent !== null);
    setHasConsent(consent === "accepted");
  }, []);

  // Stable ref for hasConsent so track callback doesn't change on consent toggle
  const hasConsentRef = useRef(hasConsent);
  hasConsentRef.current = hasConsent;

  // Track an event — stable callback (does not change when hasConsent changes)
  const track = useCallback(
    async (eventType: AnalyticsEventType) => {
      setIsLoading(true);
      try {
        const consent = hasConsentRef.current;
        await trackEvent(shareKey, {
          event_type: eventType,
          has_consent: consent,
          user_agent: consent ? navigator.userAgent : undefined,
          referer: consent ? document.referrer : undefined,
        });
      } catch (error) {
        console.error("Failed to track event:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [shareKey]
  );

  // Accept consent
  const acceptConsent = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setHasConsent(true);
    setHasChoice(true);
  }, []);

  // Decline consent
  const declineConsent = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "declined");
    setHasConsent(false);
    setHasChoice(true);
  }, []);

  return {
    hasConsent,
    hasChoice,
    track,
    acceptConsent,
    declineConsent,
    isLoading,
  };
}
